import { Box, Tooltip, Typography } from '@mui/material';
import type { MouseEvent } from 'react';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { theme } from '@/theme';
import SideNavIcon from './SideNavIcon';
import type { SideNavIconName } from './SideNavIcon';
import SideNavWrappedLabel from './SideNavWrappedLabel';
import type { SideNavItemType } from './types';

interface Props {
    item: SideNavItemType;
    isActive: boolean;
    isExpanded: boolean;
    onClick?: (ev: MouseEvent<HTMLDivElement>) => void;
    onToggleSubMenu?: (label: string | undefined) => void;
}

export default function SideNavItem({
    item,
    isActive,
    isExpanded,
    onClick,
    onToggleSubMenu,
}: Props) {
    const labelRef = useRef<HTMLSpanElement>(null);
    const [isLabelOverflowing, setIsLabelOverflowing] = useState(false);

    const onToggleSubItemsList = useCallback(
        (ev: MouseEvent<HTMLDivElement>, item: SideNavItemType) => {
            ev.stopPropagation();
            onToggleSubMenu?.(item.label);
        },
        [onToggleSubMenu],
    );

    const handleClick = (ev: MouseEvent<HTMLDivElement>, item: SideNavItemType) => {
        if (item.subItems) {
            onToggleSubItemsList(ev, item);
        } else if (item.link) {
            navigate(item.link);
        }
    };

    const hasSubItems = !!item?.subItems?.length;
    const showActiveBorder = isActive && !hasSubItems && !item.hideActiveBorder;
    const wrapLabel = item.wrapLabel && item.label;
    const showPlainLabel = isExpanded && !wrapLabel && !item.truncatedLabel && !!item.label;

    useLayoutEffect(() => {
        if (!showPlainLabel) {
            setIsLabelOverflowing(false);
            return;
        }

        const element = labelRef.current;
        if (!element) return;

        const checkOverflow = () => {
            setIsLabelOverflowing(element.scrollWidth > element.clientWidth);
        };

        checkOverflow();

        const observer = new ResizeObserver(checkOverflow);
        observer.observe(element);
        return () => observer.disconnect();
    }, [showPlainLabel, item.label]);

    const sideNavItemRowStyles = {
        display: 'grid',
        gridTemplateColumns: isExpanded ? '24px 1fr 24px' : '24px',
        alignItems: 'center',
        gap: '4px',
        minHeight: '40px',
        height: wrapLabel ? 'auto' : '40px',
        padding: wrapLabel ? '4px 0' : 'unset',
        boxSizing: 'border-box',
        justifyContent: isExpanded ? 'initial' : 'center',
    };

    const sideNavItemStyles = {
        position: 'relative',
        padding: '0 16px',
        borderLeft: `2px solid ${showActiveBorder ? theme.palette.primary.main : 'transparent'}`,
        cursor: 'pointer',
        whiteSpace: wrapLabel ? 'normal' : 'nowrap',
        color:
            isActive && !hasSubItems
                ? theme.palette.sidenav.contrastTextActive
                : theme.palette.sidenav.contrastText,
        backgroundColor: isActive && item?.activeColor ? item.activeColor : 'initial',
        width: '200px',
        boxSizing: 'border-box',
        userSelect: 'none',
        '&:hover': {
            color: theme.palette.sidenav.contrastTextActive,
        },
        overflowX: isActive && hasSubItems ? 'initial' : '',
        transition: 'width 0.2s ease-in',
    };

    const sideNavItemCollapsedStyles = {
        ...sideNavItemStyles,
        padding: 'intial',
        width: '48px',
    };
    const subItemsContainerStyles = {
        position: isExpanded ? 'initial' : 'absolute',
        padding: isExpanded ? 'initial' : '4px 0px',
        margin: isExpanded ? '3px 0 0 0' : '0px',
        top: '10px',
        left: '100%',
        borderRadius: '4px',
        display: isActive ? 'flex' : 'none',
        flexDirection: 'column',
        backgroundColor: isActive ? theme.palette.sidenav.main : 'initial',
    };

    const subItemBaseStyles = {
        cursor: 'pointer',
        zIndex: '1',
        width: '200px',
        marginBottom: '4px',
        height: isActive ? '24px' : '0px',
        boxSizing: 'border-box',
        paddingLeft: '26px',
        overflow: 'hidden',
        display: 'flex',
        alignItems: 'center',
        '&:hover': {
            color: theme.palette.sidenav.contrastTextActive,
        },
    };

    const renderIcon = (iconName: SideNavIconName | undefined, iconImageSrc?: string) => {
        if (iconImageSrc) {
            return (
                <img
                    alt=""
                    src={iconImageSrc}
                    style={{
                        width: '24px',
                        height: '24px',
                        objectFit: 'contain',
                        borderRadius: '2px',
                    }}
                />
            );
        }

        if (iconName === undefined) return null;

        return <SideNavIcon icon={iconName} />;
    };

    const navigate = useNavigate();

    // Collapsed: show label (text is hidden). Expanded: only when the label ellipsizes.
    const tooltipTitle = hasSubItems ? null : !isExpanded || isLabelOverflowing ? item.label : null;

    return (
        <Tooltip placement="right" title={tooltipTitle}>
            <Box
                onClick={(event: MouseEvent<HTMLDivElement>) =>
                    onClick ? onClick(event) : handleClick(event, item)
                }
                sx={isExpanded ? sideNavItemStyles : sideNavItemCollapsedStyles}
            >
                <Box sx={sideNavItemRowStyles}>
                    {renderIcon(item.icon, item.iconImageSrc)}
                    {isExpanded && wrapLabel && <SideNavWrappedLabel text={item.label!} />}
                    {isExpanded && !wrapLabel && item.truncatedLabel && (
                        <Tooltip title={item.label} placement="right">
                            <span>{item.truncatedLabel}</span>
                        </Tooltip>
                    )}
                    {showPlainLabel && (
                        <Box
                            ref={labelRef}
                            component="span"
                            sx={{
                                minWidth: 0,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {item.label}
                        </Box>
                    )}
                    {isExpanded && hasSubItems && (
                        <Box
                            sx={{
                                transform: `rotate(${isActive ? 180 : 0}deg)`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <svg
                                width="24"
                                height="24"
                                viewBox="0 0 24 24"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M12 15L7 10.1351L8.16667 9L12 12.7297L15.8333 9L17 10.1351L12 15Z"
                                    fill="#A8ACB3"
                                />
                            </svg>
                        </Box>
                    )}
                </Box>
                {isExpanded && hasSubItems && (
                    <Box sx={subItemsContainerStyles}>
                        {item.subItems?.map((subItem) => (
                            <Box
                                onClick={() => {
                                    if (subItem.link) {
                                        navigate(`${item.link}${subItem.link}`);
                                    }
                                }}
                                key={subItem.label}
                                sx={{
                                    ...subItemBaseStyles,
                                    color: isActive
                                        ? theme.palette.sidenav.contrastTextActive
                                        : theme.palette.sidenav.contrastText,
                                }}
                            >
                                {renderIcon(subItem.icon, subItem.iconImageSrc)}
                                <Typography variant="body1" component="span">
                                    {subItem.label}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                )}
            </Box>
        </Tooltip>
    );
}
